import { Environment } from '@/config/environment';
import {
  DESKTOP_MEDIA_BP,
  MEDIA_DOMAIN,
  MEDIA_PREFIX,
  MOBILE_MEDIA_BP,
  RECIPE_DOMAIN,
  RECIPE_PREFIX,
} from 'config/config';

import type { RecipeResult, RecipeImage, HeroBannerImages } from 'types/Recipes';

const FLAG_OPTIMIZED = '?opt';

const MOBILE_IMAGE_WIDTH = 290;
const MOBILE_IMAGE_HEIGHT = 400;
const DESKTOP_IMAGE_WIDTH = 1272;

export const transformUrlToProxyUrl = (src: string) => {
  const { IMAGE_PROXY_ENABLED } = Environment.getBuildtimeEnvVars();

  if (!src) return '';

  if (!IMAGE_PROXY_ENABLED) {
    return src;
  }

  if (src.startsWith('/') || !src.startsWith('http') || !src.startsWith('https')) {
    return src;
  }

  try {
    const tempUrl = new URL(src);
    const origin = tempUrl.origin;

    if (tempUrl?.host === MEDIA_DOMAIN) {
      return tempUrl.href.replace(origin, MEDIA_PREFIX);
    }

    if (tempUrl?.host.startsWith(RECIPE_DOMAIN)) {
      return tempUrl.href.replace(origin, RECIPE_PREFIX);
    }
  } catch (error) {
    console.error('Error while transforming src', { error, src });
    return src;
  }

  return src;
};

const getRecipeImageForHeroBanner = (recipe: RecipeResult): HeroBannerImages => {
  const images: HeroBannerImages = {
    mobile: '',
    desktop: '',
  };
  recipe?.Images?.[0]?.Formats.map((image: RecipeImage) => {
    if (image.Width === MOBILE_IMAGE_WIDTH && image.Height === MOBILE_IMAGE_HEIGHT) {
      images.mobile = transformUrlToProxyUrl(image.Url);
    }
    if (image.Width === DESKTOP_IMAGE_WIDTH) {
      images.desktop = transformUrlToProxyUrl(image.Url);
    }
  });
  return images;
};

const getPictureSources = (image?: any) => {
  const sources = [];
  if (image?.mobile) {
    sources.push({
      srcSet: getImageUrlWithCompressionParams(image.mobile, 75, image.width, image.mimeType),
      type: 'image/webp',
      media: MOBILE_MEDIA_BP,
      width: image.width,
      height: image.width,
      mimeType: image.mimeType,
    });
  }
  if (image?.mobile === '') {
    sources.push({
      srcSet: getImageUrlWithCompressionParams(
        image.tablet || image.hero || image.desktop,
        75,
        image.width,
        image.mimeType,
      ),
      type: 'image/webp',
      media: MOBILE_MEDIA_BP,
      width: image.width,
      height: image.width,
      mimeType: image.mimeType,
    });
  }
  if (image?.desktop) {
    sources.push({
      srcSet: getImageUrlWithCompressionParams(image.desktop, 75, image.width, image.mimeType),
      type: 'image/webp',
      media: DESKTOP_MEDIA_BP,
      width: image.width,
      height: image.width,
      mimeType: image.mimeType,
    });
  }
  return sources;
};

const isNextImageUnoptimized = (src: string) => {
  if (!src.includes(MEDIA_DOMAIN) && !src.includes(RECIPE_DOMAIN)) {
    return false;
  }

  return true;
};

const serveImageUnoptimized = (src: string, quality?: number, mimeType?: string) => {
  if (mimeType === 'image/svg+xml') {
    return true;
  }

  if (mimeType === 'video/mp4') {
    return true;
  }

  if (quality === 100) {
    return true;
  }

  if (!src.includes(MEDIA_DOMAIN)) {
    return true;
  }

  return false;
};

const getImageUrlWithCompressionParams = (
  src: string,
  quality: number | undefined,
  width: number | undefined,
  mimeType?: string | undefined,
) => {
  if (!src) {
    return '';
  }

  if (serveImageUnoptimized(src, quality, mimeType)) {
    return transformUrlToProxyUrl(src);
  }

  const ID = src?.split('/')?.at(-1);
  const environmentMatch = src.match(`https://${MEDIA_DOMAIN}/([^/]+)/([^/]+)/`);
  const ENVIRONMENT_ID = environmentMatch?.[1] || null;

  if (mimeType === 'image/tiff') {
    const params = [
      'compress=metadata:true',
      `quality=value:${quality || 70}`,
      'output=format:webp',
      `resize=fit:max,width:${width || 1440}`,
    ];
    return transformUrlToProxyUrl(
      `https://${MEDIA_DOMAIN}/${ENVIRONMENT_ID}/${params.join('/')}/${ID}`,
    );
  }

  const DEFAULT_SIZE = `resize=fit:max,width:${width || 1440}`;
  let providedParams = environmentMatch?.[2] || DEFAULT_SIZE;
  const hasWidthOrHeight = providedParams.includes('width:') || providedParams.includes('height:');
  if (!hasWidthOrHeight) {
    const defaultSize = `resize=fit:max,width:${width || 1440}`;
    providedParams = `${defaultSize}/${providedParams}`;
  }

  const params = [
    'compress=metadata:true',
    `quality=value:${quality || 70}`,
    'output=format:webp',
    providedParams,
  ];

  return transformUrlToProxyUrl(
    `https://${MEDIA_DOMAIN}/${ENVIRONMENT_ID}/${params.join('/')}/${ID}${FLAG_OPTIMIZED}`,
  );
};

const isAlreadyOptimized = (src: string) => {
  return src.includes(FLAG_OPTIMIZED);
};

const removeOptimizedFlag = (src: string) => {
  return src.replace(FLAG_OPTIMIZED, '');
};

/**
 * Converts relative URLs to absolute URLs for social sharing
 * Pinterest and other social platforms require absolute URLs for images
 */
const getAbsoluteUrl = (
  url: string | undefined,
  origin: string | undefined,
): string | undefined => {
  if (!url || !origin) return undefined;

  if (url.startsWith('/')) {
    return `${origin}${url}`;
  }

  return url;
};

export const ImagesService = {
  getPictureSources,
  getImageUrlWithCompressionParams,
  getRecipeImageForHeroBanner,
  transformUrlToProxyUrl,
  serveImageUnoptimized,
  isAlreadyOptimized,
  removeOptimizedFlag,
  isNextImageUnoptimized,
  getAbsoluteUrl,
};
